/**
 * The line difference of one inquiry: each inquiry line with the I2Q draft and what sales
 * quoted in F&O, and the result in one word with its icon (lead decision S2). Shared by the
 * drawer of the page "Learn from sales" and the "Quoted in F&O" block of the case page.
 *
 * The rows come computed from the server (`sales_truth.diff.line_diff`): the page only
 * prints them. A table on a wide screen, cards on a phone and in the narrow drawer and
 * Details sheet (`cards`), so the result is always in view. The prices are sales prices,
 * never a cost or a margin.
 *
 * Round 3 (20 Oct 2026, goal A): when the link has a sales order with its lines read, each
 * row that shows a quoted line carries "Ordered" or "Not ordered" beside its result, and
 * `OrderOnlyLines` lists the order lines that were not on the quotation when I2Q linked it.
 *
 * Round 4 (21 Oct 2026): with the lines of the sales order a quoted line is "Ordered",
 * "Removed" or "Cancelled", an item whose quantity changed carries "Quantity changed"
 * ("Quoted 5, ordered 3."), `OrderOnlyLines` lists the lines "Added on the sales order",
 * `CreditLines` the credit lines (they never count as ordered), and `FallbackNote` says when
 * the order lines are still the quotation lines at the order.
 */

import { Icon } from "@norican/icons";
import { Badge, DataGrid, type DataGridColumnDef, Typography } from "@norican/react";

import { ColumnCards } from "@/components/column-cards";
import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import type { LineDiffCounts, LineDiffRow, OrderLine } from "@/lib/api";
import { DIFF_ICON, type DiffVerdict } from "@/lib/line-icons";

import {
  CELL_LABELS,
  DIFF_COLUMN_LABELS,
  LFS_NOTICE_LABELS,
  ORDER_LINE_LABELS,
  qtyChangedText,
} from "./labels";
import { orderStateBadge, sideText, verdictCounts, verdictLabel, verdictTone } from "./view";

/**
 * The state of a quoted line on the order: "Ordered", "Removed", "Cancelled" (the lines of
 * the sales order) or "Ordered", "Not ordered" (the quotation lines at the order); nothing
 * when the link has no order lines.
 */
export function OrderedBadge({
  ordered,
  state,
}: {
  ordered: boolean | null | undefined;
  state?: string | null;
}) {
  const badge = orderStateBadge(ordered, state);
  if (badge === null) {
    return null;
  }
  return (
    <Badge
      tone={badge.tone}
      variant="outline"
      size="sm"
      className="whitespace-nowrap"
      title={badge.hint}
      data-ordered={badge.mark}
      data-order-state={badge.state}
    >
      {badge.label}
    </Badge>
  );
}

/** "Quantity changed" of an ordered line whose item has another quantity on the sales order. */
export function QtyChangedBadge({ row }: { row: LineDiffRow }) {
  if (typeof row.quoted_qty_total !== "number" || typeof row.ordered_qty_total !== "number") {
    return null;
  }
  return (
    <Badge
      tone="warning"
      variant="outline"
      size="sm"
      className="whitespace-nowrap"
      title={qtyChangedText(formatQty(row.quoted_qty_total), formatQty(row.ordered_qty_total))}
      data-qty-changed=""
    >
      {ORDER_LINE_LABELS.qtyChanged}
    </Badge>
  );
}

function formatQty(value: number): string {
  return value.toLocaleString("en-GB", { maximumFractionDigits: 3 });
}

/**
 * The order lines that pair with no quoted line (none: nothing): "Added on the sales order"
 * with the lines of the sales order, else the lines that were not on the quotation when I2Q
 * linked it.
 */
export function OrderOnlyLines({
  lines,
  source,
}: {
  lines: readonly OrderLine[] | null | undefined;
  source?: string | null;
}) {
  if (!lines || lines.length === 0) {
    return null;
  }
  const real = source === "sales_order";
  return (
    <div className="flex flex-col gap-1" data-order-only={real ? "added" : ""}>
      <Typography
        as="h4"
        variant="caption"
        tone="muted"
        title={real ? ORDER_LINE_LABELS.addedHint : undefined}
      >
        {real ? ORDER_LINE_LABELS.added : ORDER_LINE_LABELS.orderOnly}
      </Typography>
      <ul className="flex flex-col gap-0.5 text-sm">
        {lines.map((line, index) => (
          <li key={`${line.item}-${line.line_no ?? index}`} className="break-words tabular-nums">
            {sideText(line.item, line.qty ?? null, null)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The credit lines of the sales order (none: nothing); they never count as ordered. */
export function CreditLines({ lines }: { lines: readonly OrderLine[] | null | undefined }) {
  if (!lines || lines.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1" data-order-credit="">
      <Typography as="h4" variant="caption" tone="muted" title={ORDER_LINE_LABELS.creditHint}>
        {ORDER_LINE_LABELS.credit}
      </Typography>
      <ul className="flex flex-col gap-0.5 text-sm">
        {lines.map((line, index) => (
          <li key={`${line.item}-${line.line_no ?? index}`} className="break-words tabular-nums">
            {sideText(line.item, line.qty ?? null, null)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What the line difference does not show of the order (the drawer and "Quoted in F&O"):
 * the note of the fallback, the order lines that pair with no quoted line, the credit lines.
 */
export function OrderExtras({
  link,
}: {
  link: {
    order_lines_source: string | null;
    order_only: readonly OrderLine[];
    order_credit: readonly OrderLine[];
  };
}) {
  return (
    <>
      <FallbackNote source={link.order_lines_source} />
      <OrderOnlyLines lines={link.order_only} source={link.order_lines_source} />
      <CreditLines lines={link.order_credit} />
    </>
  );
}

/** The note of a link whose order lines are still the quotation lines at the order. */
export function FallbackNote({ source }: { source: string | null | undefined }) {
  if (source !== "quotation") {
    return null;
  }
  return (
    <Typography variant="caption" tone="muted" data-order-fallback="">
      {ORDER_LINE_LABELS.fallbackNote}
    </Typography>
  );
}

/** The result of a row: its icon (decorative) and its word in a badge. */
export function VerdictBadge({ verdict }: { verdict: string }) {
  const icon = DIFF_ICON[verdict as DiffVerdict];
  return (
    <Badge
      tone={verdictTone(verdict)}
      variant="subtle"
      size="sm"
      className="inline-flex items-center gap-1 whitespace-nowrap"
      data-verdict={verdict}
    >
      {icon && <Icon icon={icon} size="xs" />}
      {verdictLabel(verdict)}
    </Badge>
  );
}

/** The count of each result over the rows, as badges ("Same item 3"). */
export function VerdictCounts({ counts }: { counts: LineDiffCounts | null | undefined }) {
  const entries = verdictCounts(counts);
  if (entries.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-wrap gap-1.5" data-diff-counts="">
      {entries.map(({ verdict, count }) => (
        <li key={verdict}>
          <Badge tone={verdictTone(verdict)} variant="outline" size="sm" className="tabular-nums">
            {`${verdictLabel(verdict)} ${count}`}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function SalesSide({ row }: { row: LineDiffRow }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="break-words tabular-nums">
        {sideText(row.sales_item, row.sales_qty, row.sales_price)}
      </span>
      {row.sales_description && (
        <Typography as="span" variant="caption" tone="muted" className="break-words">
          {row.sales_description}
        </Typography>
      )}
    </span>
  );
}

// DS-GAP(datagrid-numeric-sans): the figures stay in the sans face with tabular-nums.
export const DIFF_COLUMNS: DataGridColumnDef<LineDiffRow>[] = [
  {
    id: "inquiry",
    header: DIFF_COLUMN_LABELS.inquiry,
    cell: (row) => (
      <span className="break-words">
        {row.inquiry_text?.trim()
          ? row.inquiry_text
          : row.verdict === "missed"
            ? CELL_LABELS.notInInquiry
            : CELL_LABELS.noText}
      </span>
    ),
  },
  {
    id: "i2q",
    header: DIFF_COLUMN_LABELS.i2q,
    cell: (row) => (
      <span className="break-words tabular-nums">
        {sideText(row.i2q_item, row.i2q_qty, row.i2q_price)}
      </span>
    ),
  },
  {
    id: "sales",
    header: DIFF_COLUMN_LABELS.sales,
    cell: (row) => <SalesSide row={row} />,
  },
  {
    id: "verdict",
    header: DIFF_COLUMN_LABELS.verdict,
    cell: (row) => (
      <span className="flex flex-wrap items-center gap-1">
        <VerdictBadge verdict={row.verdict} />
        <OrderedBadge ordered={row.ordered} state={row.order_state} />
        <QtyChangedBadge row={row} />
      </span>
    ),
  },
];

function rowId(row: LineDiffRow, index: number): string {
  return `${row.draft_index ?? "q"}-${row.sales_line_no ?? "d"}-${index}`;
}

/** The table of the line difference, with the counts above it. */
export function LineDiffTable({
  rows,
  counts,
  cards = false,
}: {
  rows: readonly LineDiffRow[];
  counts?: LineDiffCounts | null;
  /** Always cards (the drawer and the Details sheet are narrow). */
  cards?: boolean;
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  if (rows.length === 0) {
    return (
      <Typography variant="caption" tone="muted" data-diff-empty="">
        {LFS_NOTICE_LABELS.noDiff}
      </Typography>
    );
  }
  const indexed = rows.map((row, index) => ({ ...row, __id: rowId(row, index) }));
  const getRowId = (row: LineDiffRow) => (row as LineDiffRow & { __id: string }).__id;
  return (
    <div className="flex min-w-0 flex-col gap-2" data-line-diff="">
      <VerdictCounts counts={counts} />
      {narrow || cards ? (
        <ColumnCards
          columns={DIFF_COLUMNS}
          data={indexed}
          getRowId={getRowId}
          lead={["verdict"]}
          label={DIFF_COLUMN_LABELS.table}
        />
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <DataGrid<LineDiffRow>
            columns={DIFF_COLUMNS}
            data={indexed}
            getRowId={getRowId}
            density="compact"
            aria-label={DIFF_COLUMN_LABELS.table}
            className="min-w-[32rem]"
          />
        </div>
      )}
    </div>
  );
}
